import { getImageSrc } from './utils/imageHelper';

// API Base URL Configuration with intelligent defaults
// Priority: 1. Environment variable, 2. Smart detection based on hostname, 3. Development fallback
const getApiBaseUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // Smart detection based on hostname
  const hostname = window.location.hostname;
  
  // Production: sccnhub.com or new.sccnhub.com -> use api.sccnhub.com
  if (hostname.includes('sccnhub.com')) {
    return 'https://api.sccnhub.com';
  }
  
  // Development: localhost or 127.0.0.1
  return 'http://localhost:5000';
};

const API_BASE_URL = getApiBaseUrl();

const getAuthToken = () => {
  return localStorage.getItem("adminToken") || localStorage.getItem("token");
};

const handleResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  
  if (!response.ok) {
    const errorData = contentType?.includes("application/json") 
      ? await response.json() 
      : await response.text();
    const error = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    error.data = errorData;
    error.status = response.status;
    throw error;
  }

  if (contentType && contentType.includes("application/json")) {
    const data = await response.json();
    // Return full response object to preserve pagination metadata
    // Only extract data.data for simple responses without pagination
    if (data.pagination) {
      return data; // Keep full response with pagination
    }
    return data.data || data;
  }

  return await response.text();
};

const normalizeArrayResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data?.success && Array.isArray(data.data)) return data.data;
  return [];
};

const normalizeSingleResponse = (data) => {
  if (data?._id) return data;
  if (data?.data?._id) return data.data;
  if (data?.success && data.data) return data.data;
  return data || null;
};

const apiService = {
  // Newsletter API
  fetchNewslettersByType: async (type, page = 1, limit = 9) => {
    try {
      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page);
      if (limit) queryParams.append('limit', limit);
      
      const response = await fetch(
        `${API_BASE_URL}/api/newsletters/type/${type}?${queryParams.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "omit",
        }
      );
      
      // Get raw JSON to preserve pagination metadata
      const rawData = await response.json();
      
      // Return full response including pagination metadata
      if (rawData && rawData.pagination) {
        return {
          data: normalizeArrayResponse(rawData),
          pagination: rawData.pagination
        };
      }
      
      // Fallback for backward compatibility
      return {
        data: normalizeArrayResponse(rawData),
        pagination: null
      };
    } catch (error) {
      console.error(`Error fetching ${type} newsletters:`, error);
      throw error;
    }
  },

  // Add this function to your apiService object
fetchPublicCampuses: async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("status", "approved");
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.search) queryParams.append("search", params.search);
    
    const response = await fetch(
      `${API_BASE_URL}/api/universities/public?${queryParams.toString()}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    
    const result = await handleResponse(response);
    
    // Format the response to match what your component expects
    if (result && typeof result === 'object') {
      return {
        success: true,
        data: {
          data: Array.isArray(result) ? result : (result.data || []),
          pagination: result.pagination || {
            current: params.page || 1,
            total: 1,
            totalRecords: Array.isArray(result) ? result.length : (result.data ? result.data.length : 0),
          }
        },
        message: "Data fetched successfully"
      };
    }
    
    return {
      success: false,
      data: {
        data: [],
        pagination: {
          current: params.page || 1,
          total: 0,
          totalRecords: 0,
        }
      },
      message: "No data available"
    };
    
  } catch (error) {
    console.error("API Error:", error);
    
    return {
      success: false,
      data: {
        data: [],
        pagination: {
          current: params.page || 1,
          total: 0,
          totalRecords: 0,
        }
      },
      message: error.message || "Failed to fetch campuses"
    };
  }
},

  // Blogs API
  fetchBlogs: async (page = 1, limit = 9, search = '', filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page);
      if (limit) queryParams.append('limit', limit);
      if (search) queryParams.append('search', search);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      
      const response = await fetch(`${API_BASE_URL}/api/blogs?${queryParams.toString()}`);
      
      // Get raw JSON to preserve pagination metadata
      const rawData = await response.json();
      
      // Return full response including pagination metadata
      if (rawData && rawData.pagination) {
        return {
          data: normalizeArrayResponse(rawData),
          pagination: rawData.pagination
        };
      }
      
      // Fallback for backward compatibility
      return {
        data: normalizeArrayResponse(rawData),
        pagination: null
      };
    } catch (error) {
      console.error('Error fetching blogs:', error);
      throw error;
    }
  },

  fetchBlogBySlug: async (slug) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/blogs/${slug}`);
      const data = await handleResponse(response);
      return normalizeSingleResponse(data);
    } catch (error) {
      console.error(`Error fetching blog with slug ${slug}:`, error);
      throw error;
    }
  },

  fetchNewsletterBySlug: async (slug) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/newsletters/slug/${slug}`);
      const data = await handleResponse(response);
      return normalizeSingleResponse(data);
    } catch (error) {
      console.error(`Error fetching newsletter with slug ${slug}:`, error);
      throw error;
    }
  },

  // Team Members API
   fetchTeamMembers: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/team-members`);
      const data = await handleResponse(response);
      
      // Ensure we always return an array
      const normalized = normalizeArrayResponse(data);
      if (!Array.isArray(normalized)) {
        console.warn('Team members data is not an array:', normalized);
        return [];
      }
      return normalized;
    } catch (error) {
      console.error("Error fetching team members:", error);
      // Return empty array instead of throwing error
      return [];
    }
  },

  fetchTeamMemberById: async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/team-members/${id}`);
      const data = await handleResponse(response);
      return normalizeSingleResponse(data);
    } catch (error) {
      console.error(`Error fetching team member with ID ${id}:`, error);
      throw error;
    }
  },

  // Events API
  fetchEvents: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.type) queryParams.append('type', params.type); // 'upcoming' or 'past'
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      
      const queryString = queryParams.toString();
      const response = await fetch(
        `${API_BASE_URL}/api/events${queryString ? `?${queryString}` : ''}`
      );
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error("Error fetching events:", error);
      throw error;
    }
  },

  fetchEventBySlug: async (slug) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${slug}`);
      const data = await handleResponse(response);
      return normalizeSingleResponse(data);
    } catch (error) {
      console.error(`Error fetching event ${slug}:`, error);
      throw error;
    }
  },

  // Feedback API
  submitFeedback: async (feedbackData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(feedbackData),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error("Feedback submission error:", error);
      throw error;
    }
  },

  // Newsletter Subscription API
  subscribeNewsletter: async (subscriptionData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/newsletter-subscriptions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(subscriptionData),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      throw error;
    }
  },

  // Contact Form API
  submitContactForm: async (contactData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(contactData),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error("Contact form submission error:", error);
      throw error;
    }
  },

  // Partnership Inquiry API
  submitPartnership: async (partnershipData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/partnerships`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(partnershipData),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error("Partnership submission error:", error);
      throw error;
    }
  },

  // Latest Updates API
  fetchLatestUpdates: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/latest-updates/active`);
      const data = await handleResponse(response);
      return normalizeSingleResponse(data);
    } catch (error) {
      console.error("Error fetching latest updates:", error);
      throw error;
    }
  },

  // Fetch active announcements (optionally filter by days)
  fetchActiveAnnouncements: async (limit = 50) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/latest-updates/active?limit=${limit}`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error("Error fetching active announcements:", error);
      return [];
    }
  },

  // Careers API
  fetchCareers: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/careers`);
      const data = await handleResponse(response);
      
      // Ensure we always return an array
      const normalized = normalizeArrayResponse(data);
      if (!Array.isArray(normalized)) {
        console.warn('Careers data is not an array:', normalized);
        return [];
      }
      return normalized;
    } catch (error) {
      console.error("Error fetching careers:", error);
      // Return empty array instead of throwing error
      return [];
    }
  },

  fetchCareerBySlug: async (slug) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/careers/slug/${slug}`);
      const data = await handleResponse(response);
      return normalizeSingleResponse(data);
    } catch (error) {
      console.error(`Error fetching career with slug ${slug}:`, error);
      throw error;
    }
  },

  submitCareerApplication: async (applicationData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/career-applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });
      const data = await handleResponse(response);
      return normalizeSingleResponse(data);
    } catch (error) {
      console.error('Error submitting career application:', error);
      throw error;
    }
  },

  // Universities API
  fetchApprovedCampuses: async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("status", "approved");
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.search) queryParams.append("search", params.search);
    
    const headers = {
      "Content-Type": "application/json",
    };
    
    // Optional: Include token if available (for additional features), but don't require it
    const token = getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    const response = await fetch(
      `${API_BASE_URL}/api/universities?${queryParams.toString()}`,
      {
        headers,
      }
    );
    
    const result = await handleResponse(response);
    
    // Backend returns: { success, message, data: [...], pagination: {...} }
    // Return it directly - DO NOT re-wrap
    return result;
    
  } catch (error) {
    console.error("API Error:", error);
    
    // Return consistent error format
    return {
      success: false,
      data: [],
      pagination: {
        current: params.page || 1,
        total: 0,
        totalRecords: 0,
      },
      message: error.message || "Failed to fetch approved campuses"
    };
  }
},

 // University Registration API
  registerUniversity: async (universityData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/universities/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(universityData),
      });

      const data = await handleResponse(response);
      return {
        success: true,
        token: data.token,
        university: data.university
      };
    } catch (error) {
      console.error("University registration error:", error);
      throw new Error(
        error.data?.error || 
        error.data?.message || 
        error.message || 
        "Registration failed. Please try again."
      );
    }
  },

  // Get Universities with Pagination
  getUniversitiesPaginated: async (page = 1, limit = 20, search = '') => {
    try {
      const params = new URLSearchParams({
        status: 'approved',
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (search) {
        params.append('search', search);
      }

      const response = await fetch(`${API_BASE_URL}/api/universities?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await handleResponse(response);
      return {
        universities: data.data || [],
        pagination: data.pagination || {
          current: 1,
          total: 1,
          count: 0,
          totalItems: 0
        }
      };
    } catch (error) {
      console.error("Get universities error:", error);
      return {
        universities: [],
        pagination: {
          current: 1,
          total: 1,
          count: 0,
          totalItems: 0
        }
      };
    }
  },

  // Email Check API for Universities
  checkUniversityEmailExists: async (email) => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/universities/check-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });

      const data = await handleResponse(response);
      return data.exists || false;
    } catch (error) {
      console.error("University email check error:", error);
      return false;
    }
  },

  // Student Registration API
  registerStudent: async (studentData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/students/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(studentData),
      });

      const data = await handleResponse(response);
      return {
        success: true,
        token: data.token,
        student: data.student
      };
    } catch (error) {
      console.error("Student registration error:", error);
      throw new Error(
        error.data?.error || 
        error.data?.message || 
        error.message || 
        "Registration failed. Please try again."
      );
    }
  },

  // Fetch Approved Students (Youth Ambassadors)
  fetchApprovedStudents: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("status", "approved");
      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.search) queryParams.append("search", params.search);
      
      const response = await fetch(
        `${API_BASE_URL}/api/students?${queryParams.toString()}`
      );
      
      const result = await handleResponse(response);
      
      // Return in consistent format
      if (result && result.data) {
        return {
          success: true,
          data: result.data,
          pagination: result.pagination
        };
      }
      
      return {
        success: true,
        data: Array.isArray(result) ? result : [],
        pagination: null
      };
    } catch (error) {
      console.error("Error fetching approved students:", error);
      return {
        success: false,
        data: [],
        pagination: null
      };
    }
  },

  // Email Check API
  checkEmailExists: async (email) => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/students/check-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });

      const data = await handleResponse(response);
      return data.exists || false;
    } catch (error) {
      console.error("Email check error:", error);
      return false;
    }
  },

  // Mentors API
  fetchMentors: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/mentors`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching mentors:', error);
      throw error;
    }
  },

  // Advisors API
  fetchAdvisors: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advisors`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching advisors:', error);
      throw error;
    }
  },

  // Experts API
  fetchExperts: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/experts`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching experts:', error);
      throw error;
    }
  },

  // Popups API
  fetchActivePopup: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/popups/active`);
      
      // Handle 404 or no active popup
      if (response.status === 404) {
        return null;
      }
      
      if (!response.ok) {
        console.warn('Popup fetch failed with status:', response.status);
        return null;
      }
      
      const data = await handleResponse(response);
      
      // Validate data structure before returning
      if (data && typeof data === 'object' && data._id) {
        return data;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching active popup:', error);
      // Return null instead of throwing to prevent app crashes
      return null;
    }
  },

  // Resources API
  getYouthToolkits: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/youth-toolkits`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching youth toolkits:', error);
      throw error;
    }
  },

  getCampusToolkits: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/campus-toolkits`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching campus toolkits:', error);
      throw error;
    }
  },

  getPolicyPapers: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/policy-papers`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching policy papers:', error);
      throw error;
    }
  },

  getHandbooks: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/handbooks`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching handbooks:', error);
      throw error;
    }
  },

  getDownloads: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/downloads`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching downloads:', error);
      throw error;
    }
  },

  // Events API Extensions
  getUpcomingEvents: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/upcoming`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      throw error;
    }
  },

  getPastEvents: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/past`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching past events:', error);
      throw error;
    }
  },

  getEventReports: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/reports`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching event reports:', error);
      throw error;
    }
  },

  // Media & Insights API
  getGTFNews: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/media/gtf-news`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching GTF news:', error);
      throw error;
    }
  },

  getPressReleases: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/press-releases`);
      const data = await handleResponse(response);
      const normalized = normalizeArrayResponse(data);
      return normalized.map(item => ({
        ...item,
        thumbnail: getImageSrc(item.thumbnail),
      }));
    } catch (error) {
      console.error('Error fetching press releases:', error);
      // Return empty array instead of throwing to prevent crashes
      return [];
    }
  },

  getPressReleaseBySlug: async (slug) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/press-releases/${slug}`);
      const data = await handleResponse(response);
      return normalizeSingleResponse(data);
    } catch (error) {
      console.error('Error fetching press release:', error);
      throw error;
    }
  },

  getGtfSpotlights: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gtf-spotlight`);
      const data = await handleResponse(response);
      const normalized = normalizeArrayResponse(data);
      
      // Ensure each spotlight has valid structure
      return normalized.map(spotlight => ({
        ...spotlight,
        thumbnail: getImageSrc(spotlight.thumbnail)
      }));
    } catch (error) {
      console.error('Error fetching GTF spotlights:', error);
      // Return empty array instead of throwing to prevent crashes
      return [];
    }
  },

  getInstagramFeed: async (limit = 6) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/instagram/feed?limit=${limit}`);
      const data = await handleResponse(response);
      
      // Return posts array directly, with graceful fallback
      if (data && data.posts && Array.isArray(data.posts)) {
        return data.posts;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching Instagram feed:', error);
      // Return empty array on error to allow graceful degradation
      return [];
    }
  },

  getDrShendeArticles: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/media/articles`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching Dr. Shende articles:', error);
      throw error;
    }
  },

  getCOPFeatures: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/media/cop-features`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching COP features:', error);
      throw error;
    }
  },

  getMediaGalleries: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/media/galleries`);
      const data = await handleResponse(response);
      return normalizeArrayResponse(data);
    } catch (error) {
      console.error('Error fetching media galleries:', error);
      throw error;
    }
  },

  // Platform Statistics
  _statsCache: null,
  _statsCacheTime: 0,
  fetchStats: async function() {
    const TTL = 5 * 60 * 1000; // 5 minutes
    if (this._statsCache && Date.now() - this._statsCacheTime < TTL) {
      return this._statsCache;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/stats`, {
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "omit",
      });
      const data = await handleResponse(response);
      this._statsCache = data;
      this._statsCacheTime = Date.now();
      return data;
    } catch (error) {
      console.error('Error fetching platform statistics:', error);
      throw error;
    }
  },

  // Campus map data grouped by state/country
  _mapDataCache: null,
  _mapDataCacheTime: 0,
  fetchMapData: async function() {
    const TTL = 10 * 60 * 1000; // 10 minutes
    if (this._mapDataCache && Date.now() - this._mapDataCacheTime < TTL) {
      return this._mapDataCache;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/universities/map-data`, {
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
      });
      const data = await handleResponse(response);
      const result = data?.data || data;
      this._mapDataCache = result;
      this._mapDataCacheTime = Date.now();
      return result;
    } catch (error) {
      console.error('Error fetching map data:', error);
      return null;
    }
  },

  // Student Profile Management
  updateStudent: async (studentId, studentData, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/students/${studentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(studentData)
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data: normalizeSingleResponse(data)
      };
    } catch (error) {
      console.error('Error updating student:', error);
      throw error;
    }
  },

  deleteStudent: async (studentId, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/students/${studentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting student:', error);
      throw error;
    }
  },

  // University Profile Management
  updateUniversity: async (universityId, universityData, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/universities/${universityId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(universityData)
      });
      const data = await handleResponse(response);
      return {
        success: true,
        data: normalizeSingleResponse(data)
      };
    } catch (error) {
      console.error('Error updating university:', error);
      throw error;
    }
  },

  deleteUniversity: async (universityId, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/universities/${universityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting university:', error);
      throw error;
    }
  }
};

export default apiService;